// Backend · Alerts: the display vocabulary every tab and drawer shares, and the
// one-line rule summary the Rules tab and the protected-rule drawer both show.

import type {
  AlertCategory,
  AlertKind,
  AlertMetric,
  AlertMetricCatalogEntry,
  AlertRule,
  AlertSeverity,
  Comparator,
  MetricGuards,
  MetricWindow,
} from '@/api/types';

export const SEV_COLOR: Readonly<Record<string, string>> = {
  critical: 'var(--bad)', error: 'var(--bad)', warn: 'var(--warn)',
};
export const CAT_LABEL: Readonly<Record<AlertCategory, string>> = {
  system: 'System', backfill_quality: 'Backfill quality', pixel: 'Pixel monitoring',
  campaign_pacing: 'Campaign pacing', roas: 'ROAS',
};
export const KIND_LABEL: Readonly<Record<AlertKind, string>> = {
  error_spike: 'Error spike', new_error: 'New error', silence: 'Silence',
  job_failure: 'Job failure', metric_breach: 'Metric breach',
};

/** Every comparator the server accepts, with its display label. */
export const COMPARATOR_LABEL: Readonly<Record<Comparator, string>> = {
  gt: '> greater than', gte: '≥ at least', lt: '< less than', lte: '≤ at most',
  eq: '= equal to', ne: '≠ not equal to',
};
/** The comparators the editor offers for a new choice. */
export const COMPARATORS: readonly { id: Comparator; label: string }[] = (
  ['gt', 'gte', 'lt', 'lte'] as const
).map((id) => ({ id, label: COMPARATOR_LABEL[id] }));

/** The day-granular window kinds a backfill rule picks from. */
export type DayWindowKind = Exclude<MetricWindow['kind'], 'rolling_hours'>;
/** A backfill rule's window: every `MetricWindow` arm except the hour window. */
export type DayWindow = Exclude<MetricWindow, { kind: 'rolling_hours' }>;
/** The rolling length the backfill picker offers. */
export const ROLLING_DAYS_DEFAULT = 7;

export function rollingDaysLabel(n: number): string {
  return `Rolling ${n} ${n === 1 ? 'day' : 'days'}`;
}
export function rollingHoursLabel(n: number): string {
  return `Last ${n} ${n === 1 ? 'hour' : 'hours'}`;
}

export const WINDOWS: readonly { id: DayWindowKind; label: string }[] = [
  // ids MUST match the MetricWindow serde tags (types.gen.ts) verbatim —
  // buildBody sends { kind: windowKind } as-is and the server 4xxes unknowns.
  { id: 'aggregate30d', label: '30-day aggregate' },
  { id: 'latest_complete_day', label: 'Latest complete day' },
  { id: 'rolling_days', label: rollingDaysLabel(ROLLING_DAYS_DEFAULT) },
];
// Hour-granular windows for pixel rules ({kind:'rolling_hours', n}).
export const PIXEL_WINDOWS: readonly { n: number; label: string }[] = [1, 6, 24]
  .map((n) => ({ n, label: rollingHoursLabel(n) }));

export function isDayWindow(window: MetricWindow | null | undefined): window is DayWindow {
  return !!window && window.kind !== 'rolling_hours';
}

function listedDayWindow(kind: DayWindowKind): DayWindow {
  return kind === 'rolling_days' ? { kind, n: ROLLING_DAYS_DEFAULT } : { kind };
}

/**
 * The backfill window picker's `<select>` value: the kind, or
 * `rolling_days:<n>` for a rolling length the picker does not list.
 */
export function dayWindowValue(window: DayWindow): string {
  return window.kind === 'rolling_days' && window.n !== ROLLING_DAYS_DEFAULT
    ? `rolling_days:${window.n}`
    : window.kind;
}

export interface DayWindowChoice {
  value: string;
  label: string;
  window: DayWindow;
}

/**
 * The backfill window picker's options: the listed windows, then each window
 * in `keep` the list does not carry (a rule saved through the API with a
 * 1-day rolling window). The editor keeps the saved window and the current
 * draft here, so a window the picker does not list shows as itself and is
 * sent back unchanged instead of being widened to the listed 7 days.
 */
export function dayWindowChoices(keep: readonly (DayWindow | null | undefined)[]): readonly DayWindowChoice[] {
  const choices: DayWindowChoice[] = WINDOWS.map((w) => ({ value: w.id, label: w.label, window: listedDayWindow(w.id) }));
  for (const window of keep) {
    if (!window || choices.some((choice) => choice.value === dayWindowValue(window))) continue;
    choices.push({
      value: dayWindowValue(window),
      label: window.kind === 'rolling_days' ? rollingDaysLabel(window.n) : window.kind,
      window,
    });
  }
  return choices;
}

/**
 * The pixel window picker's options: the listed hour windows plus each length
 * in `keep` the list does not carry, in order of length. Without it a saved
 * 12-hour window rendered as "Last 1 hour" while the editor still sent 12.
 */
export function hourWindowChoices(keep: readonly (number | null | undefined)[]): readonly { n: number; label: string }[] {
  const lengths = new Set(PIXEL_WINDOWS.map((w) => w.n));
  for (const n of keep) if (n != null) lengths.add(n);
  return [...lengths].sort((a, b) => a - b).map((n) => ({ n, label: rollingHoursLabel(n) }));
}

/**
 * The condition picker's options: the offered comparators plus each of `keep`
 * they do not list (`=` and `≠` exist only on rules saved through the API),
 * so a saved comparator shows as itself instead of as "> greater than".
 */
export function comparatorChoices(keep: readonly (Comparator | null | undefined)[]): readonly { id: Comparator; label: string }[] {
  const choices = [...COMPARATORS];
  for (const id of keep) {
    if (id && !choices.some((choice) => choice.id === id)) choices.push({ id, label: COMPARATOR_LABEL[id] });
  }
  return choices;
}
export const SEVERITIES: readonly { id: AlertSeverity; label: string }[] = [
  { id: 'warn', label: 'Warn' }, { id: 'error', label: 'Error' }, { id: 'critical', label: 'Critical' },
];

/** The log detectors the editor can build (job failures are seed-only). */
export type LogDetector = 'error_spike' | 'new_error' | 'silence';

// Narrowing guards for values read back from a <select>: each accepts exactly
// the ids its picker lists, so a value off the list never reaches a body.
export function isAlertCategory(value: string | undefined): value is AlertCategory {
  return value !== undefined && Object.prototype.hasOwnProperty.call(CAT_LABEL, value);
}
export function isAlertSeverity(value: string | undefined): value is AlertSeverity {
  return SEVERITIES.some((severity) => severity.id === value);
}
export function isComparator(value: string | undefined): value is Comparator {
  return value !== undefined && Object.prototype.hasOwnProperty.call(COMPARATOR_LABEL, value);
}
export function isLogDetector(value: string | undefined): value is LogDetector {
  return value === 'error_spike' || value === 'new_error' || value === 'silence';
}

/**
 * Every field any `AlertTrigger` arm carries, each optional. A saved trigger is
 * one arm of the union; the editors read whichever fields that arm has, so they
 * read it through this flat view instead of narrowing arm by arm.
 */
export interface TriggerFields {
  type?: string;
  component?: string | null;
  min_count?: number;
  window_minutes?: number;
  silence_minutes?: number;
  metric?: AlertMetric;
  op?: Comparator;
  threshold?: number;
  warn?: number;
  critical?: number;
  window?: MetricWindow;
  guards?: MetricGuards;
}

// `MetricMeta.unit` is a wire token (`pct` | `count` | `hours` | `secs` |
// `ratio` | `cents` | `bool`), not display text. Render it, never print it raw —
// otherwise the editor reads "Threshold (pct)" / "Threshold (bool)".
const UNIT_SUFFIX: Readonly<Record<string, string>> = { pct: '%', hours: 'h', secs: 's', ratio: '×', cents: '¢' };
export function unitSuffix(unit: string | null | undefined): string { return (unit && UNIT_SUFFIX[unit]) ?? ''; }
const UNIT_LABEL: Readonly<Record<string, string>> = {
  pct: '%', hours: 'hours', secs: 'seconds', ratio: 'ratio', cents: 'cents',
  count: 'count', bool: '0 = any failure',
};
export function unitLabel(unit: string | null | undefined, fallback: string): string {
  return (unit && UNIT_LABEL[unit]) ?? fallback;
}

export function summarizeRule(
  r: Pick<AlertRule, 'scope' | 'trigger'>,
  metricCatalog: readonly AlertMetricCatalogEntry[] = [],
): string {
  const t: TriggerFields = r.trigger || {};
  const scope = r.scope?.level === 'production' ? (r.scope.production_id || 'all productions')
    : r.scope?.level === 'pixel' ? (r.scope.pixel_id || 'all pixels')
    : 'global';
  if (t.type === 'metric') {
    const m = metricCatalog.find((entry) => entry.metric === t.metric);
    const op = (t.op && COMPARATOR_LABEL[t.op]?.[0]) ?? t.op;
    const win = t.window?.kind === 'rolling_hours' ? ` / ${t.window.n}h` : '';
    return `${scope} · ${m?.label ?? t.metric} ${op} ${t.threshold}${unitSuffix(m?.unit)}${win}`;
  }
  if (t.type === 'system_metric') {
    const win = t.window?.kind === 'rolling_hours' ? ` / ${t.window.n}h` : '';
    return `${scope} · warn ${t.warn} · critical ${t.critical}${win}`;
  }
  if (t.type === 'error_spike') return `${scope} · ≥ ${t.min_count} errors / ${t.window_minutes}m`;
  if (t.type === 'new_error') return `${scope} · new error signature`;
  if (t.type === 'silence') return `${scope} · silent > ${t.silence_minutes}m`;
  return scope;
}
